import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  LocalHospital,
  ArrowBack,
  ExitToApp,
  AttachMoney,
  PersonSearch,
  BarChart,
} from '@mui/icons-material';
import { authService } from '../../../services/api';

const analyticsMenuItems = [
  {
    title: 'Revenue Dashboard',
    description: 'Billing revenue, collection trends and payment mode breakdown',
    icon: <AttachMoney sx={{ fontSize: 50 }} />,
    path: '/admin/analytics/revenue',
    color: '#2e7d32',
  },
  {
    title: 'Doctor Performance',
    description: 'Patient volume, appointment completion and OPD/IPD stats per doctor',
    icon: <PersonSearch sx={{ fontSize: 50 }} />,
    path: '/admin/analytics/doctor-performance',
    color: '#1565c0',
  },
  {
    title: 'Payroll & Expense Dashboard',
    description: 'Monthly payroll cost, other expenses breakdown and operating cost trends',
    icon: <BarChart sx={{ fontSize: 50 }} />,
    path: '/admin/analytics/payroll-expense',
    color: '#283593',
  },
];

const HospitalAnalyticsPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = React.useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Hospital Analytics
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName} ({user?.role})
            </Typography>
            <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}
        >
          Analytics &amp; Reports
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 5 }}
        >
          Select a dashboard to view detailed insights
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {analyticsMenuItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
                  },
                }}
                onClick={() => navigate(item.path)}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                  <Box sx={{ color: item.color, mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      backgroundColor: item.color,
                      '&:hover': { backgroundColor: item.color, filter: 'brightness(0.9)' },
                    }}
                  >
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
};

export default HospitalAnalyticsPage;
